<template>
  <div class="stock-page">
    <KTCodePreview v-bind:title="'Báo cáo lãi lỗ theo hoá đơn'">
      <template v-slot:preview>
        <div>
          <BillRevenueReportFilter
            :initial-filter="initialFilterData"
            :stores="stores"
            :options="billTypeOptions"
            :loading="onLoading"
            :has-export-permission="checkPermission('BILL_REVENUE_REPORT_EXPORT')"
            @filter="handleFilter"
            @refresh="handleRefresh"
            @export="handleExport"
          />
          <BillRevenueReportTable
            class="mt-3"
            :items="reportData"
            :loading="onLoading"
            :total-count="totalCount"
            :current-page="apiParams.page"
            :per-page="apiParams.limit"
            :summary="summaryData"
            :last-sync-at="lastSyncAt"
            @page-change="handlePageChange"
          />
        </div>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue'
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module'
import moment from 'moment'
import { currencyMask, convertPrice } from '@/utils/common'
import { makeToastFaile } from '@/utils/common'

import BillRevenueReportFilter from './components/BillRevenueReportFilter.vue'
import BillRevenueReportTable from './components/BillRevenueReportTable.vue'
import BillRevenueReportService from './services/BillRevenueReportService.js'
import { BILL_TYPE_NAME } from '@/utils/enum-name'
import { showLoadingDialog, closeSwal } from '@/utils/sweet-alert2'
import permissionMixin from '@/view/mixins/permission.mixin';


export default {
  mixins: [permissionMixin],
  data() {
    return {
      onLoading: false,
      mask: currencyMask,
      listProductType: [
        { id: null, name: 'Tất cả' },
        { id: 1, name: 'Sản phẩm' },
        { id: 2, name: 'Sản phẩm theo IMEI' },
      ],
      stores: [],
      billTypeOptions: [],
      initialFilterData: {},
      apiParams: {
        page: 1,
        limit: 10,
        startDate: null,
        endDate: null,
        storeId: null,
        billType: null,
        billNumber: '',
      },
      reportData: [],
      summaryData: null,
      lastSyncAt: null,
      totalCount: 0,
    }
  },
  components: {
    KTCodePreview,
    BillRevenueReportFilter,
    BillRevenueReportTable,
  },
  computed: {},

  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Tồn kho', route: '/productStock' },
      { title: 'Danh sách tồn kho' },
    ])
  },
  created() {
    this.initializeBillRevenueReport()
  },
  methods: {
    async initializeBillRevenueReport() {
      // Default filter: last 30 days (match BillRevenueReportFilter preset)
      const today = moment()
      const startDate = today.clone().subtract(29, 'days').format('DD/MM/YYYY')
      const endDate = today.format('DD/MM/YYYY')

      this.billTypeOptions = [
        { value: null, text: 'Tất cả loại hoá đơn' },
        ...Object.entries(BILL_TYPE_NAME).map(([value, text]) => ({
          value: Number(value),
          text,
        })),
      ]

      this.initialFilterData = {
        startDate,
        endDate,
        storeId: null,
        billType: null,
        billNumber: '',
      }

      this.apiParams = {
        ...this.apiParams,
        page: 1,
        limit: this.apiParams.limit || 10,
        startDate,
        endDate,
        storeId: null,
        billType: null,
        billNumber: '',
      }

      try {
        this.stores = await BillRevenueReportService.fetchStoresByUser()
        await this.fetchSyncState()
        await this.fetchReportData()
      } catch (error) {
        this.onLoading = false
        makeToastFaile(error.response?.data?.message || error.message || 'Lỗi')
      }
    },

    async fetchSyncState() {
      try {
        const syncState = await BillRevenueReportService.fetchSyncState()
        this.lastSyncAt = syncState?.lastSyncAt || null
      } catch (e) {
        // Không chặn render table nếu không lấy được sync time
        this.lastSyncAt = null
      }
    },

    async fetchReportData(options = {}) {
      const { skipTableLoading = false } = options
      if (!skipTableLoading) {
        this.onLoading = true
      }
      try {
        const [listData, summaryData] = await Promise.all([
          BillRevenueReportService.fetchReportList(this.apiParams),
          BillRevenueReportService.fetchReportSummary(this.apiParams),
        ])

        this.reportData = listData?.dataset || []
        this.totalCount = listData?.totalRow || 0
        this.summaryData = summaryData || null
      } catch (error) {
        makeToastFaile(error.response?.data?.message || error.message || 'Lỗi')
      } finally {
        if (!skipTableLoading) {
          this.onLoading = false
        }
      }
    },

    handleFilter(filterData) {
      this.apiParams = {
        ...this.apiParams,
        page: 1,
        startDate: filterData.startDate,
        endDate: filterData.endDate,
        storeId: filterData.storeId,
        billType: filterData.billType,
        billNumber: filterData.billNumber,
      }
      this.fetchReportData()
    },

    handlePageChange(page) {
      this.apiParams = { ...this.apiParams, page }
      this.fetchReportData()
    },

    async handleRefresh() {
      showLoadingDialog('Đang đồng bộ dữ liệu...', 'Vui lòng chờ...')
      try {
        await BillRevenueReportService.syncData()
        await this.fetchSyncState()
        await this.fetchReportData({ skipTableLoading: true })
      } catch (error) {
        makeToastFaile(error.response?.data?.message || error.message || 'Lỗi')
      } finally {
        closeSwal()
      }
    },

    async handleExport() {
      try {
        await BillRevenueReportService.exportExcel(this.apiParams)
      } catch (error) {
        makeToastFaile(error.response?.data?.message || error.message || 'Lỗi')
      }
    },
    convertPrice,
  },
}
</script>

<style scope>
.d-flex {
  display: -webkit-box !important;
}
</style>
<style lang="scss" scoped>
.stock-page {
  table {
    margin: 0;
    border-collapse: collapse;
    border-spacing: 0;
    table-layout: fixed;
    th {
      position: relative;
      z-index: 1;
    }

    td,
    th {
      vertical-align: middle;
      padding: 0.75rem;
    }

    th:first-child {
      position: sticky !important;
      left: 0;
      z-index: 2;
      background-color: white;
    }
    th:nth-child(2) {
      position: sticky !important;
      left: 250px;
      z-index: 2;
      background-color: white;
    }

    td:first-child {
      position: sticky;
      left: 0;
      z-index: 0;
      background-color: white;
    }

    td:nth-child(2) {
      position: sticky;
      left: 250px;
      z-index: 0;
      background-color: white;
    }

    .font-size-setting {
      font-size: 0.86rem;
    }
  }
  .labelInput {
    font-weight: 600;
  }

  .btn.btn-icon.btn-sm,
  .btn-group-sm > .btn.btn-icon {
    height: calc(1.35em + 1.1rem + 2px);
    width: auto;
  }
  .page-item.disabled {
    cursor: not-allowed;
    &.bv-d-xs-down-none {
      .page-link {
        margin: 0.35rem 0.25rem;
        text-align: center;
      }
    }
  }

  .icon:hover {
    background-color: #90c6fc;
  }

  table.bill-data-table {
    th:first-child,
    th:nth-child(2) {
      position: sticky !important;
      top: 0 !important;
      left: auto !important;
      z-index: 2 !important;
      background-color: #f8f9fa;
    }

    td:first-child,
    td:nth-child(2) {
      position: static !important;
      left: auto !important;
      z-index: auto !important;
      background-color: inherit;
    }
  }
}
</style>